import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useRegistrationFeeConfig } from "../hooks/useRegistrationFeeConfig";
import { showToast } from "../../../../core/utils/alert";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";

interface FeeConfigModalProps {
  type?: "config";
  id?: string | null;
  courseId?: number;
  attachedCenters?: {
    id: number;
    name: string;
    code: string;
    type: string;
    address: string;
    district: string;
    status: string;
  }[];
}

const FeeConfigModal: React.FC<FeeConfigModalProps> = ({
  id,
  attachedCenters = [],
  courseId,
}) => {
  const { attachCenters, detachCenters, deleteFeeConfig } =
    useRegistrationFeeConfig();
  const { getCenterState } = useCenter();
  const { centers } = getCenterState();
  const [selectedAttachCenters, setSelectedAttachCenters] = useState<number[]>(
    []
  );
  const [selectedDetachCenters, setSelectedDetachCenters] = useState<number[]>(
    []
  );

  // console.log(centers, 'centers----')
  // console.log(attachedCenters, 'attachedCenters----')
  // console.log(courseId, 'courseId----')

  const handleDelete = async () => {
    if (id) {
      const result = await deleteFeeConfig(id);
      if (result.success) {
        showToast({
          title: "Success",
          text: result.data?.message || "Record deleted successfully",
          icon: "success",
        });
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not delete the record",
          icon: "error",
        });
      }
    }
  };

  const handleAttach = async () => {
    if (selectedAttachCenters.length > 0) {
      const result = await attachCenters(id!, selectedAttachCenters);
      if (result.success) {
        showToast({
          title: "Success",
          text: `${selectedAttachCenters.length} center(s) attached successfully`,
          icon: "success",
        });
        setSelectedAttachCenters([]);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not attach centers",
          icon: "error",
        });
      }
    }
  };

  const handleDetach = async () => {
    if (selectedDetachCenters.length > 0) {
      const result = await detachCenters(id!, selectedDetachCenters);
      if (result.success) {
        showToast({
          title: "Success",
          text: "Centers detached successfully",
          icon: "success",
        });
        setSelectedDetachCenters([]);
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not detach centers",
          icon: "error",
        });
      }
    }
  };

  return (
    <>
      {/* Delete Fee Configuration Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-trash" />
              </span>
              <h4>Confirm Deletion</h4>
              <p>
                You want to delete this fee configuration, this can't be undone
                once you delete.
              </p>
              <div className="d-flex justify-content-center">
                <Link
                  to="#"
                  className="btn btn-light me-3"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  to="#"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={handleDelete}
                >
                  Yes, Delete
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attach Modal */}
      <div className="modal fade" id="attach-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <h4>Attach Centers</h4>
              {centers.every(
                (center) =>
                  Array.isArray(center.courseRegistrationFees) &&
                  center.courseRegistrationFees.some(
                    (crf: any) => crf.course_id === courseId
                  )
              ) ? (
                <div className="alert alert-info">
                  <i className="ti ti-info-circle me-2"></i>
                  All centers are already attached to this registration fee
                  configuration.
                </div>
              ) : (
                <>
                  {centers.some(
                    (center) =>
                      Array.isArray(center.courseRegistrationFees) &&
                      center.courseRegistrationFees.some(
                        (crf: any) => crf.course_id === courseId
                      )
                  ) && (
                    <div className="alert alert-warning mb-3">
                      <i className="ti ti-alert-circle me-2"></i>
                      Selected centers have existing fee configurations
                    </div>
                  )}
                  <div className="center-list-container">
                    {centers.map((center) => {
                      const hasRegistrationFee =
                        Array.isArray(center.courseRegistrationFees) &&
                        center.courseRegistrationFees.some(
                          (crf: any) => crf.course_id === courseId
                        );
                      return (
                        <div key={center.id} className="center-item">
                          <input
                            type="checkbox"
                            id={`attach-${center.id}`}
                            value={center.id}
                            disabled={hasRegistrationFee}
                            onChange={(e) => {
                              if (!hasRegistrationFee) {
                                const value = Number(e.target.value);
                                setSelectedAttachCenters((prev) =>
                                  e.target.checked
                                    ? [...prev, value]
                                    : prev.filter((id) => id !== value)
                                );
                              }
                            }}
                            checked={
                              !hasRegistrationFee &&
                              selectedAttachCenters.includes(center.id)
                            }
                          />
                          <label htmlFor={`attach-${center.id}`}>
                            {center.name}
                            {hasRegistrationFee && (
                              <small className="text-info ms-2">
                                <i className="fas fa-check-circle"></i>
                              </small>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <div className="d-flex justify-content-center mt-3">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-success"
                      data-bs-dismiss="modal"
                      style={{
                        opacity: selectedAttachCenters.length === 0 ? 0.5 : 1,
                      }}
                      onClick={(e) => {
                        if (selectedAttachCenters.length === 0) {
                          e.preventDefault();
                        } else {
                          handleAttach();
                        }
                      }}
                    >
                      Attach
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Detach Modal */}
      <div className="modal fade" id="detach-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <h4>Detach Centers</h4>
              {!Array.isArray(attachedCenters) ||
              attachedCenters.length === 0 ? (
                <div className="alert alert-warning">
                  <i className="ti ti-info-circle me-2"></i>
                  No centers are attached to this registration fee
                  configuration.
                </div>
              ) : (
                <>
                  <div className="center-list-container">
                    {attachedCenters.map((center) => (
                      <div key={center.id} className="center-item">
                        <input
                          type="checkbox"
                          id={`detach-${center.id}`}
                          value={center.id}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            setSelectedDetachCenters((prev) =>
                              e.target.checked
                                ? [...prev, value]
                                : prev.filter((id) => id !== value)
                            );
                          }}
                          checked={selectedDetachCenters.includes(center.id)}
                        />
                        <label htmlFor={`detach-${center.id}`}>
                          {center.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex justify-content-center mt-3">
                    <Link
                      to="#"
                      className="btn btn-light me-2"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                      style={{
                        opacity: selectedDetachCenters.length === 0 ? 0.5 : 1,
                      }}
                      onClick={(e) => {
                        if (selectedDetachCenters.length === 0) {
                          e.preventDefault();
                        } else {
                          handleDetach();
                        }
                      }}
                    >
                      Detach
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .center-list-container {
          max-height: 300px;
          overflow-y: auto;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 5px;
        }
        .scrollable-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .center-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px;
          background: #f9f9f9;
          border-radius: 5px;
        }
        .center-item input[type="checkbox"] {
          transform: scale(1.2);
        }
        .center-item label {
          font-size: 16px;
        }
      `}</style>
    </>
  );
};

export default FeeConfigModal;
